import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import logo from "@/assets/brand/simbolo.svg";
import { safeNextPath } from "@/lib/oauthConsent";

const loginSchema = z.object({
  email: z.string().trim().email({ message: "Email inválido" }),
  password: z.string().min(6, { message: "Senha deve ter pelo menos 6 caracteres" }),
});

type LoginFormValues = z.infer<typeof loginSchema>;

const inputCls = "h-[42px] rounded-[10px] bg-muted text-[13.5px]";
const labelCls = "text-xs font-semibold text-muted-foreground";

const AdminLogin = () => {
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<"login" | "forgot">("login");
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // ?next= volta para onde o login foi pedido (ex.: /oauth/consent do Claude);
  // só caminho interno, para não virar open redirect.
  const nextPath = safeNextPath(searchParams.get("next")) ?? "/admin";
  const { signIn, resetPassword } = useAuth();
  const { toast } = useToast();

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });

  const onSubmit = async (data: LoginFormValues) => {
    setLoading(true);
    try {
      const { error } = await signIn(data.email, data.password);
      if (error) {
        toast({
          title: error.message.includes("Invalid login credentials")
            ? "Credenciais inválidas"
            : "Erro ao entrar",
          description: error.message.includes("Invalid login credentials")
            ? "Email ou senha incorretos."
            : error.message,
          variant: "destructive",
        });
      } else {
        setTimeout(() => navigate(nextPath), 500);
      }
    } catch {
      toast({
        title: "Erro inesperado",
        description: "Ocorreu um erro. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const onForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = forgotEmail.trim();
    if (!email) return;
    setLoading(true);
    const { error } = await resetPassword(email);
    setLoading(false);
    if (error) {
      toast({
        title: "Não foi possível enviar",
        description: error.message,
        variant: "destructive",
      });
      return;
    }
    setForgotSent(true);
  };

  return (
    <div className="grid min-h-screen place-items-center bg-background p-4">
      <div className="flex w-full max-w-[400px] flex-col gap-[22px]">
        <div className="flex flex-col items-center gap-3.5">
          <img src={logo} alt="" className="h-[52px] w-[52px] object-contain" />
          <div className="text-center">
            <h1 className="mb-[5px] text-[22px] font-bold tracking-[-0.02em]">
              {mode === "login" ? "Acessar o painel" : "Recuperar senha"}
            </h1>
            <p className="text-[13px] text-muted-foreground">Área restrita · eliveutonsouza.com</p>
          </div>
        </div>

        <div className="flex flex-col gap-4 rounded-2xl border border-border bg-card p-[26px] shadow-card">
          {mode === "login" ? (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem className="space-y-[7px]">
                      <FormLabel className={labelCls}>E-mail</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="seu@email.com"
                          className={inputCls}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem className="space-y-[7px]">
                      <FormLabel className={labelCls}>Senha</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder="••••••••"
                          className={inputCls}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  type="submit"
                  className="h-11 w-full rounded-[11px] text-[13.5px] font-bold"
                  disabled={loading}
                >
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Entrar
                </Button>
                <button
                  type="button"
                  onClick={() => {
                    setMode("forgot");
                    setForgotEmail(form.getValues("email"));
                  }}
                  className="text-center text-[12.5px] font-semibold text-primary hover:underline"
                >
                  Esqueci minha senha
                </button>
              </form>
            </Form>
          ) : forgotSent ? (
            <div className="flex flex-col gap-4 text-center">
              <p className="text-[13.5px] leading-relaxed text-muted-foreground">
                Se existir uma conta para <strong className="text-foreground">{forgotEmail}</strong>
                , enviamos um link para redefinir a senha. Confira sua caixa de entrada.
              </p>
              <button
                type="button"
                onClick={() => {
                  setMode("login");
                  setForgotSent(false);
                }}
                className="text-[12.5px] font-semibold text-primary hover:underline"
              >
                Voltar ao login
              </button>
            </div>
          ) : (
            <form onSubmit={onForgot} className="flex flex-col gap-4">
              <div className="space-y-[7px]">
                <label className={labelCls} htmlFor="forgot-email">
                  E-mail
                </label>
                <Input
                  id="forgot-email"
                  type="email"
                  placeholder="seu@email.com"
                  className={inputCls}
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  required
                />
              </div>
              <Button
                type="submit"
                className="h-11 w-full rounded-[11px] text-[13.5px] font-bold"
                disabled={loading}
              >
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Enviar link de recuperação
              </Button>
              <button
                type="button"
                onClick={() => setMode("login")}
                className="text-center text-[12.5px] font-semibold text-primary hover:underline"
              >
                Voltar ao login
              </button>
            </form>
          )}
        </div>

        <Link
          to="/"
          className="text-center text-[12.5px] text-muted-foreground transition-colors hover:text-foreground"
        >
          ← Voltar ao site
        </Link>
      </div>
    </div>
  );
};

export default AdminLogin;
